import { useEffect, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

import { useAuthStore } from '@/stores/auth-store'
import { resetSessionAndClearQuery } from '@/lib/session-logout'

const IDLE_TIMEOUT_MS = 60 * 60 * 1000
const ACTIVITY_STORAGE_KEY = 'dialclear_last_user_activity_at'

// Avoid excessive localStorage writes while the user is actively moving/typing.
const ACTIVITY_STORAGE_THROTTLE_MS = 5000
const CHECK_INTERVAL_MS = 30_000

type ActivityEvent =
  | 'mousemove'
  | 'mousedown'
  | 'keydown'
  | 'scroll'
  | 'touchstart'

function getStoredActivityAt(): number | null {
  try {
    const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}

function setStoredActivityAt(ts: number) {
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(ts))
  } catch {
    // Ignore storage failures (private mode / quota).
  }
}

export function IdleTimeoutGuard() {
  const queryClient = useQueryClient()
  const token = useAuthStore((s) => s.auth.getEffectiveAccessToken())
  const logoutOnceRef = useRef(false)

  const activityEvents = useMemo<ActivityEvent[]>(
    () => ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'],
    [],
  )

  useEffect(() => {
    if (!token) return
    if (window.location.pathname.includes('/sign-in')) return

    const now = Date.now()
    const stored = getStoredActivityAt()
    const initialActivityAt = stored && stored > 0 ? stored : now

    let lastActivityAt = initialActivityAt
    let lastStorageWriteAt = initialActivityAt

    // Initialize stored value so other tabs don't immediately time out.
    setStoredActivityAt(initialActivityAt)

    const onStorage = (e: StorageEvent) => {
      if (e.key !== ACTIVITY_STORAGE_KEY) return
      if (!e.newValue) return
      const n = Number(e.newValue)
      if (!Number.isFinite(n)) return
      lastActivityAt = n
    }

    const markActivity = () => {
      const ts = Date.now()
      lastActivityAt = ts

      if (ts - lastStorageWriteAt >= ACTIVITY_STORAGE_THROTTLE_MS) {
        lastStorageWriteAt = ts
        setStoredActivityAt(ts)
      }
    }

    const onAnyActivity = () => markActivity()

    activityEvents.forEach((evt) => window.addEventListener(evt, onAnyActivity, { passive: true }))
    window.addEventListener('storage', onStorage)

    const intervalId = window.setInterval(() => {
      if (logoutOnceRef.current) return

      const idleFor = Date.now() - lastActivityAt
      if (idleFor < IDLE_TIMEOUT_MS) return

      logoutOnceRef.current = true
      resetSessionAndClearQuery(queryClient)
      toast.error('Session timed out due to inactivity.')
      window.location.href = '/sign-in'
    }, CHECK_INTERVAL_MS)

    return () => {
      window.removeEventListener('storage', onStorage)
      activityEvents.forEach((evt) => window.removeEventListener(evt, onAnyActivity))
      window.clearInterval(intervalId)
    }
  }, [token, activityEvents, queryClient])

  return null
}

